import { useTenantApi } from '@/account/hook/useTenantApi';
import { createSecurePayment, type CreateSecurePaymentRequest, type CreateSecurePaymentResponse } from '@/api/registration/payment';
import { type ApiResponseSingle } from '@/api/tenantClient';
import { UiButton, UiHStack, UiSpinner, UiStack, UiText, UiVStack } from '@/lib/ui';
import { useBoolean, useTheme } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Form } from 'react-router-dom';
import { useScript } from 'usehooks-ts';

const scriptId = 'securepay-ui-js';
const containerId = 'securepay-ui-container';
const allowedCardTypes = ['visa', 'mastercard'];
interface PayBySecurePayIframeProps {
  setErrors: React.Dispatch<React.SetStateAction<string[]>>
  isLoading: boolean
  isDisabled: boolean
  merchantCode: string
  clientId: string
  orderId: number
  testMode: boolean
  resultCallback: () => void
}

const PayBySecurePayIframe: React.FC<PayBySecurePayIframeProps> = ({
  setErrors,
  isLoading,
  orderId,
  isDisabled,
  clientId,
  merchantCode,
  resultCallback,
  testMode
}) => {
  const securePayUIRef = useRef<any>(null);
  const [isFormValidity, setFormValidity] = useState<boolean>(false);
  const [isLoadIFrame, { off: hideLoadIFrame }] = useBoolean(true);
  const [isSubmitting, { on: showSubmitting, off: hideSubmitting }] = useBoolean(false);
  const sdkUrl = testMode ? 'https://payments-stest.npe.auspost.zone/v3/ui/client/securepay-ui.min.js' : 'https://payments.auspost.net.au/v3/ui/client/securepay-ui.min.js';
  const scriptStatus = useScript(sdkUrl, { id: scriptId });
  const theme = useTheme();
  const { createTenantApiRequest } = useTenantApi();

  const { mutate: createPaymentMutate, isLoading: isCreatePaymentLoading } = useMutation<ApiResponseSingle<CreateSecurePaymentResponse>, Error, CreateSecurePaymentRequest>({
    mutationFn: async (preparePaymentData: CreateSecurePaymentRequest) => {
      return await createSecurePayment(createTenantApiRequest)(preparePaymentData);
    },
    onSuccess: (result) => {
      if (result.errors && result.errors.length > 0) {
        setErrors(result.errors);
      } else {
        hideSubmitting();
        resultCallback();
      }
    },
    onError: (error: any) => {
      hideSubmitting();
      setErrors([error.message ?? 'Failed to completed registrations']);
    }
  });

  useEffect(() => {
    if (scriptStatus === 'ready') {
      if ((window as any).securePayUI) {
        const gray = theme.colors.gray[100];
        // eslint-disable-next-line new-cap
        securePayUIRef.current = new (window as any).securePayUI.init({
          containerId,
          scriptId,
          clientId,
          merchantCode,
          card: {
            allowedCardTypes,
            showCardIcons: true,
            onCardTypeChange: (cardType: string) => {},
            onBINChange: (cardBIN: string) => {},
            onFormValidityChange: (valid: boolean) => {
              setFormValidity(valid);
              if (valid) {
                setErrors([]);
              }
            },
            onTokeniseSuccess: (tokenisedCard: any) => {
              /* tokenisedCard
              bin: "424242"
              createdAt: "2024-09-10T03:37:29.471Z"
              expiryMonth: "12"
              expiryYear: "34"
              last4: "242"
              merchantCode: "5AR0055"
              scheme: "visa"
              token: "6171725424400183"
              */
              hideSubmitting();
              createPaymentMutate({ token: `${tokenisedCard.token}`, orderId });
            },
            onTokeniseError: (errors: any) => {
              hideSubmitting();
              setErrors(['Error tokenising card']);
            }
          },
          style: {
            backgroundColor: gray,
          },
          onLoadComplete: () => {
            hideLoadIFrame();
            // SecurePay UI loaded successfully
          }
        });
      }
    }
  }, [clientId, hideSubmitting, merchantCode, scriptStatus]);

  const handleSubmit = () => {
    if (securePayUIRef.current) {
      showSubmitting();
      securePayUIRef.current.tokenise();
    }
  };

  const handleReset = () => {
    if (securePayUIRef.current) {
      securePayUIRef.current.reset();
    }
  };

  return (
    <UiVStack alignItems={'stretch'} w='100%'>
      <Form onSubmit={(e) => { return e.preventDefault(); }}
        style={{ width: '100%' }}>
        <UiStack id={containerId} />
        <UiHStack
          spacing={4}
          justifyContent={'right'}>
          {isLoadIFrame && <UiSpinner />}
          {/* {!isLoadIFrame && <UiButton type="button" variant='ghost' onClick={handleReset}>Reset</UiButton>} */}
          {!isLoadIFrame && <UiButton
            mt={4}
            px={8}
            size={'lg'}
            shadow={'base'}
            colorScheme={'primary'}
            type="button"
            onClick={handleSubmit}
            isDisabled={!isFormValidity || isDisabled}
            isLoading={isSubmitting || isLoading}
          >Pay</UiButton>}

        </UiHStack>
      </Form>
      {/** Add full screen loading here */}
      { (isSubmitting || isCreatePaymentLoading) &&
        <UiStack
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 9999,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }}
          justifyContent="center"
          alignItems="center"
        >
          <UiVStack justifyContent="center" alignItems="center">
            <UiSpinner color="yellow" />
            <UiText mt={4} color="yellow" variant='body1' textAlign={'center'}>
              {'Your transaction is being processed...'}
            </UiText>
            <UiText color="yellow" variant='body1' textAlign={'center'}>
              {'Please wait a moment and avoid refreshing or closing this page.'}
            </UiText>
          </UiVStack>
        </UiStack>
      }
    </UiVStack>
  );
};

export default PayBySecurePayIframe;
